/* The phone's ＋: Next availability, as a sheet.
 *
 * On the desk's board this list is always on screen, above the grid, because
 * there is room for it there and because a menu you can see is a menu you use.
 * A phone has 660px of window and the desk layout spends 400 of them before the
 * first technician — so the menu goes behind a button, and the button goes under
 * the thumb. It is the one thing on the phone that is hidden rather than shrunk,
 * and it is hidden because it is used a few times an hour while the board under
 * it is read continuously.
 *
 * What it does NOT lose in the move: every mark the desk rows carry. The wait,
 * the technician who would get it, the ⇄ that says taking it means moving work
 * off somebody, the ☾ that says it would run past closing. Those are the reasons
 * a service is or is not a good idea right now, and a phone standing on the
 * floor needs them more than the desk does, not less.
 *
 * Two ways out of a row, and the difference matters:
 *
 *   the row     start it, with whoever the rotation says. Exactly what pressing
 *               the service's key does on the desk — same function, see
 *               store.bookNextUp — because it is the same act, and the list is
 *               named after the promise that the board picks the technician.
 *   the  ＞     arm it and close, so a row on the board can be tapped instead.
 *               The desk's shift-held press, for a phone with no shift.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const ServiceSheet = {
  name: 'ServiceSheet',
  setup: function () {
    // Who each service would go to, for the whole menu in one pass — the same
    // computed the desk bar runs, and the same reason: three lookups per row,
    // each walking the rotation, is a list that re-derives the day per paint.
    var nextUp = Vue.computed(function () {
      var out = {};
      store.quickAddServices.value.forEach(function (svc) {
        out[svc.id] = store.nextUpDetail(svc.id);
      });
      return out;
    });

    function close() { store.ui.serviceSheet = false; }

    /* Start it, and say so.
     *
     * The toast is not decoration. On the desk the act has a shape you watched
     * happen — a square appears in a column you were already looking at. Here
     * the sheet is over the board when the tap lands and the square goes up
     * behind it, so without a sentence the only evidence is a sheet closing.
     *
     * `bookNextUp` does the refusing: a shut book, a service nobody in today
     * does, a walk-in that would run into a booking. Each of those has its own
     * answer on screen already — the notice, the warning sheet, the clash sheet
     * — so a false return here is something already said, and saying it twice
     * would be the phone talking over its own dialogue.
     */
    function start(svc) {
      // Whether anybody was actually free, read before the tap changes it.
      var wasFree = store.presentStaff.value.some(function (s) {
        return !store.isBusy(s.id);
      });
      var before = store.state.turns.length;
      if (!store.bookNextUp(svc.id)) {
        // Armed but unplaced — request mode, or nobody free with room. The
        // sheet has to get out of the way either way, because what happens
        // next is a tap on a row underneath it.
        if (store.ui.armedServiceId) close();
        return;
      }
      close();
      // Who it landed on, read off the square that just went up rather than off
      // the suggestion that preceded it: `bookTurn` has its own last word about
      // where a turn goes, and a toast naming somebody else would be the phone
      // reporting its intention instead of the result.
      var placed = store.state.turns.length > before
        ? store.state.turns[store.state.turns.length - 1]
        : null;
      var who = placed ? store.staffOf(placed.staffId) : null;
      store.toast(
        wasFree
          ? svc.name + ' started' + (who ? ' — ' + who.name : '') + '.'
          // Nobody was free and a square went up anyway, which is the rotation
          // handing it to whoever is least behind. The desk reads that off the
          // grid at a glance; a phone has to be told.
          : svc.name + ' started' + (who ? ' — ' + who.name : '') +
            ', though nobody was free.',
        wasFree ? 'ok' : 'warn');
    }

    // Arm and stand back. The board's rows light up the way its squares do, and
    // the desk picks the technician themselves.
    function arm(svc) {
      close();
      store.armService(svc.id);
    }

    // What the button is worth and how long it usually runs. The desk reads
    // this off a hover card; a phone has no hover, so it goes on the row.
    function meta(svc) {
      var bits = [];
      if (svc.turnWeight !== 1) bits.push(store.weightLabel(svc.turnWeight));
      if (svc.defaultMinutes) bits.push(svc.defaultMinutes + ' min');
      return bits.join(' · ');
    }

    return {
      ui: store.ui,
      services: store.quickAddServices,
      nextUp: nextUp,
      close: close,
      start: start,
      arm: arm,
      meta: meta,
      weightBadge: store.weightBadge,
      weightLabel: store.weightLabel,
      requestsOn: Vue.computed(function () { return store.ui.requestsOn; }),
      requestMode: Vue.computed(function () { return store.ui.requestMode; })
    };
  },
  template: [
    '<div class="psheet-scrim" @click.self="close">',
    '  <div class="psheet" role="dialog" aria-label="Start a turn">',
    '    <div class="psheet__grip" aria-hidden="true"></div>',
    '    <div class="psheet__head">',
    '      <h3>Start a turn</h3>',
    /* No Request switch: requests come from Mangomint (Kevin, 2026-10-19). */
    '      <button type="button" class="psheet__x" @click="close"',
    '        aria-label="Close">&times;</button>',
    '    </div>',
    '',
    '    <p v-if="requestMode" class="psheet__note">',
    '      A request — pick the service, then tap the technician they asked for.',
    '    </p>',
    '',
    '    <ul class="psheet__list">',
    '      <li v-for="svc in services" :key="svc.id" class="prow"',
    '        :class="{ \'prow--none\': nextUp[svc.id].kind === \'none\',',
    '                  \'prow--late\': nextUp[svc.id].closing,',
    '                  \'prow--shut\': nextUp[svc.id].closing && nextUp[svc.id].closing.blocked }"',
    '        :style="{ \'--chip\': svc.color }">',
    '        <button type="button" class="prow__main" @click="start(svc)">',
    '          <span class="prow__letter">{{ svc.abbrev }}</span>',
    '          <span class="prow__text">',
    '            <span class="prow__name">{{ svc.name }}',
    '              <span v-if="svc.turnWeight !== 1" class="prow__half"',
    '                :class="{ \'is-zero\': svc.turnWeight === 0 }"',
    '                :aria-label="weightLabel(svc.turnWeight)">{{ weightBadge(svc.turnWeight) }}</span>',
    '              <span v-if="nextUp[svc.id].moves.length" class="prow__moves"',
    '                aria-label="Taking this means moving work off that technician">&#8644;</span>',
    '              <span v-if="nextUp[svc.id].closing" class="prow__late"',
    '                aria-label="Would run past closing">&#9790;</span>',
    '            </span>',
    '            <span class="prow__sub">',
    '              <span class="prow__who"',
    '                :class="{ \'is-none\': nextUp[svc.id].kind === \'none\' || nextUp[svc.id].kind === \'wait\',',
    '                          \'is-wait\': nextUp[svc.id].kind === \'eta\' }">{{ nextUp[svc.id].name }}</span>',
    '              <span v-if="nextUp[svc.id].wait" class="prow__eta">{{ nextUp[svc.id].wait }}</span>',
    '              <span v-if="meta(svc)" class="prow__meta">{{ meta(svc) }}</span>',
    '            </span>',
    '          </span>',
    '        </button>',
    /* Its own target, and a wide one: it is the difference between the board
       choosing and you choosing, which is not a thing to hit by accident on
       either side. */
    '        <button type="button" class="prow__pick" @click="arm(svc)"',
    '          :title="\'Pick the technician for \' + svc.name + \' yourself\'"',
    '          :aria-label="\'Choose who does \' + svc.name">&#8250;</button>',
    '      </li>',
    '    </ul>',
    '',
    '    <p v-if="!services.length" class="psheet__empty">',
    '      No services are set up yet — Settings &rarr; Services.',
    '    </p>',
    '  </div>',
    '</div>'
  ].join('\n')
};
